import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useData } from "../../DataContext";
import { generateId } from "../../utils/id";
import { apiRequest } from "../../api/http";

import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";

type Props = {
  projectId: string;
  onChanged?: () => void | Promise<void>;
};

type ApiPayment = {
  id: string;
  tenant_id: string;
  project_id: string;
  amount: number;
  payment_method: "Cash" | "Bank";
  date: string;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
};

type UiPayment = {
  id: string;
  projectId: string;
  amount: number;
  paymentMethod: "Cash" | "Bank";
  date: string;
  notes?: string;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function toUi(row: ApiPayment): UiPayment {
  return {
    id: row.id,
    projectId: row.project_id,
    amount: Number(row.amount) || 0,
    paymentMethod: row.payment_method,
    date: row.date,
    notes: row.notes || undefined,
  };
}

function sum(list: UiPayment[]) {
  return list.reduce((s, p) => s + (Number(p.amount) || 0), 0);
}

export default function PaymentsReceivedTab({ projectId, onChanged }: Props) {
  const { t } = useTranslation();
  const { tenantId, refreshProjectData } = useData() as any;

  const [payments, setPayments] = useState<UiPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Form state
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"Cash" | "Bank">("Cash");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await apiRequest<ApiOk<ApiPayment[]> | ApiErr>(
        "GET",
        `/api/payments_received?project_id=${encodeURIComponent(projectId)}`,
        { tenantId }
      );

      if ((res as any).ok) {
        const rows = ((res as any).data || []) as ApiPayment[];
        setPayments(rows.map(toUi));
      } else {
        setErr((res as any).error || t("paymentsReceived.tab.loadError"));
      }
    } catch (e: any) {
      setErr(e?.message || t("paymentsReceived.tab.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, projectId]);

  const total = useMemo(() => sum(payments), [payments]);

  const byMethod = useMemo(() => {
    const cash = payments
      .filter((p) => p.paymentMethod === "Cash")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);

    const bank = payments
      .filter((p) => p.paymentMethod === "Bank")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);

    return { cash, bank };
  }, [payments]);

  const handleAdd = async () => {
    if (!amount || !date) {
      alert(t("paymentsReceived.tab.amountDateRequired"));
      return;
    }

    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      alert(t("paymentsReceived.amountInvalid"));
      return;
    }

    const newId = generateId("PAY");

    try {
      const res = await apiRequest<ApiOk<ApiPayment> | ApiErr>(
        "POST",
        "/api/payments_received",
        {
          tenantId,
          body: {
            id: newId,
            project_id: projectId,
            amount: amt,
            payment_method: paymentMethod,
            date,
            notes: notes.trim() || null,
          },
        }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("paymentsReceived.tab.addError"));
        return;
      }

      const created = toUi((res as any).data as ApiPayment);
      setPayments((prev) => [created, ...prev]);
      await refreshProjectData(projectId);

      setAmount("");
      setPaymentMethod("Cash");
      setDate("");
      setNotes("");
    } catch (e: any) {
      alert(e?.message || t("paymentsReceived.tab.addError"));
    }
  };

  return (
    <div className="container" style={{ padding: 0 }}>
      <style>{`
        .payGrid {
          display: grid;
          gap: 12px;
          align-items: end;
          grid-template-columns:
            minmax(220px, 1.1fr)
            minmax(220px, 0.9fr)
            minmax(220px, 0.9fr)
            minmax(220px, 1.3fr);
        }
        .payCell { display: flex; flex-direction: column; min-width: 0; }

        @media (max-width: 1180px) { .payGrid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 680px) { .payGrid { grid-template-columns: 1fr; } }

        .fieldLabel {
          color: var(--muted);
          font-weight: 900;
          font-size: 12px;
          margin-bottom: 6px;
        }
        .fieldInput {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(15,23,42,0.12);
          background: rgba(255,255,255,0.85);
          outline: none;
          box-sizing: border-box;
        }
        .fieldInput:focus {
          border-color: rgba(15,23,42,0.22);
          box-shadow: 0 0 0 3px rgba(15,23,42,0.08);
        }

        .payHeaderRight {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .pillBtn {
          border-radius: 999px;
          padding: 8px 12px;
          font-weight: 900;
          line-height: 1;
          white-space: nowrap;
        }

        .payTotalsRow {
          display: grid;
          gap: 10px;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin-top: 12px;
        }
        @media (max-width: 980px) { .payTotalsRow { grid-template-columns: 1fr; } }

        .miniRow {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          padding: 8px 10px;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.70);
        }
        .miniLabel { color: var(--muted); font-weight: 900; font-size: 12px; }
        .miniValue { font-weight: 1000; letter-spacing: -0.2px; }
        .numRight { text-align: right; }
      `}</style>

      <Card
        title={
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 1000, letterSpacing: "-0.2px" }}>{t("paymentsReceived.title")}</div>

            <div className="payHeaderRight">
              <div className="badge" title={t("paymentsReceived.tab.totalTitle")}>
                {t("common.total")}: <span style={{ fontWeight: 1000 }}>{money(total)}</span>
              </div>

              <button className="btn btnSm pillBtn" onClick={handleAdd} type="button" title={t("paymentsReceived.tab.addTitle")}>
                {t("paymentsReceived.addBtn")}
              </button>
            </div>
          </div>
        }
        subtitle={t("paymentsReceived.tab.subtitle")}
      >
        {err ? <div style={{ color: "var(--bad)", fontWeight: 800, marginBottom: 10 }}>{err}</div> : null}

        <div className="payGrid">
          <div className="payCell">
            <div className="fieldLabel">{t("common.amount")}</div>
            <input
              className="fieldInput"
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className="payCell">
            <div className="fieldLabel">{t("common.method")}</div>
            <select
              className="fieldInput"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as "Cash" | "Bank")}
            >
              <option value="Cash">{t("common.cash")}</option>
              <option value="Bank">{t("common.bank")}</option>
            </select>
          </div>

          <div className="payCell">
            <div className="fieldLabel">{t("common.date")}</div>
            <input className="fieldInput" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div className="payCell">
            <div className="fieldLabel">{t("common.notes")}</div>
            <input
              className="fieldInput"
              placeholder={t("paymentsReceived.tab.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="payTotalsRow">
          <div className="miniRow">
            <div className="miniLabel">{t("paymentsReceived.tab.cashReceived")}</div>
            <div className="miniValue numGood">{money(byMethod.cash)}</div>
          </div>
          <div className="miniRow">
            <div className="miniLabel">{t("paymentsReceived.tab.bankReceived")}</div>
            <div className="miniValue numGood">{money(byMethod.bank)}</div>
          </div>
          <div className="miniRow">
            <div className="miniLabel">{t("paymentsReceived.tab.totalReceived")}</div>
            <div className="miniValue numGood">{money(total)}</div>
          </div>
        </div>
      </Card>

      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("paymentsReceived.tab.historyTitle")}
          subtitle={
            loading
              ? t("paymentsReceived.tab.loadingMsg")
              : payments.length
              ? t("paymentsReceived.tab.paymentCount", { count: payments.length })
              : t("paymentsReceived.tab.noPaymentsYet")
          }
          right={
            <span className="badge">
              {t("common.total")}: <b>{money(total)}</b>
            </span>
          }
        >
          {payments.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "10px 2px" }}>
              {t("paymentsReceived.tab.empty")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 140 }}>{t("common.date")}</th>
                  <th style={{ width: 120 }}>{t("common.method")}</th>
                  <th style={{ width: 160 }} className="numRight">
                    {t("common.amount")}
                  </th>
                  <th>{t("common.notes")}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ whiteSpace: "nowrap" }}>{p.date}</td>
                    <td>
                      <span className={`badge ${p.paymentMethod === "Cash" ? "" : "badgeGood"}`}>
                        {p.paymentMethod === "Cash" ? t("common.cash") : t("common.bank")}
                      </span>
                    </td>
                    <td className="numRight" style={{ whiteSpace: "nowrap", fontWeight: 1000 }}>
                      {money(p.amount)}
                    </td>
                    <td>
                      <CellClamp lines={2} title={p.notes || ""}>
                        {p.notes || <span style={{ color: "var(--muted)" }}>—</span>}
                      </CellClamp>
                    </td>
                  </tr>
                ))}

                <tr>
                  <td colSpan={2} style={{ fontWeight: 1000, textAlign: "right" }}>
                    {t("paymentsReceived.tab.totalReceived")}
                  </td>
                  <td className="numRight" style={{ fontWeight: 1000, whiteSpace: "nowrap" }}>
                    {money(total)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </TableShell>
      </div>
    </div>
  );
}
